import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Amplify } from 'aws-amplify';
import type { ResourcesConfig } from 'aws-amplify';
import AppRoutes from './routes/Routes';
import { awsConfig } from './aws-config/aws-config';
import { setConfigLoadFailed } from './store/slices/config/configSlice';
import { useLocation } from 'react-router-dom';
import IconIndicator from './components/user-feedback/IconIndicator';
import { globals } from './common/globals';
import { fetchConfig } from './store/slices/config/configThunks';
import { validateSession } from './store/slices/auth/authThunks';
import { cancelPendingAuth } from './store/slices/auth/authSlice';
import { selectIsAuthInProgress } from './store/slices/auth/authSelectors';
import { clearEmail } from './common/tokenPersistence';
import { selectConfigExpressSettings } from './store/slices/config/configSelectors';
import MaintainancePage from './pages/Maintenance';
import useDocumentTitle from './hooks/useDocumentTitle';
import { useAppDispatch } from './store/store';

type AwsEnv = keyof typeof awsConfig;
const awsEnv = process.env.REACT_APP_ENVIRONMENT as AwsEnv;
const resolvedAwsConfig = awsConfig[awsEnv] as ResourcesConfig;

globals.awsConfig = resolvedAwsConfig;
Amplify.configure(resolvedAwsConfig);

const App = () => {
  const dispatch = useAppDispatch();
  globals.dispatch = dispatch;

  const location = useLocation();

  const [lastPlace, setLastPlace] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  const settings = useSelector(selectConfigExpressSettings);
  const isAuthInProgress = useSelector(selectIsAuthInProgress);

  useDocumentTitle();

  useEffect(() => {
    dispatch(validateSession());
  }, [dispatch]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAuthInProgress) {
        dispatch(cancelPendingAuth());
        clearEmail();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch, isAuthInProgress]);

  useEffect(() => {
    const loadConfig = async () => {
      setLoadingConfig(true);
      await dispatch(setConfigLoadFailed(false));

      const cityIdentiferPath = location.pathname.split('/')[1];
      if (cityIdentiferPath !== lastPlace) {
        setLastPlace(cityIdentiferPath);
        try {
          await dispatch(fetchConfig(cityIdentiferPath));
        } catch {
          await dispatch(setConfigLoadFailed(true));
        }
      }

      setLoadingConfig(false);
    };
    loadConfig();
  }, [location.pathname, dispatch, lastPlace]);

  return (
    <>
      {loadingConfig && (
        <div className="loading-container">
          <IconIndicator />
        </div>
      )}
      {!loadingConfig && (
        <>
          {!settings?.under_maintenance && <AppRoutes />}
          {settings?.under_maintenance && <MaintainancePage />}
        </>
      )}
    </>
  );
};

export default App;
